import React from "react";
import { Link } from "react-router-dom";

export const ACCENT = "#7C6BFF"; // Pinlayr blue↔purple accent
export const ACCENT_GRADIENT = "linear-gradient(135deg,#4F7CFF 0%,#8B5CF6 100%)";

export function Logo({ dark = false, className = "" }) {
  return (
    <Link to="/" className={`inline-flex items-center gap-2.5 ${className}`}>
      <span
        className="grid h-8 w-8 place-items-center rounded-lg text-[17px] font-bold text-white shadow-[0_2px_10px_rgba(124,107,255,0.45)]"
        style={{ background: ACCENT_GRADIENT }}
      >
        P
      </span>
      <span
        className={`text-[19px] font-semibold tracking-tight ${
          dark ? "text-[#F4F5F8]" : "text-[#F4F5F8]"
        }`}
      >
        Pinlayr
      </span>
    </Link>
  );
}

export function Button({ children, loading, disabled, variant = "primary", className = "", style, ...props }) {
  const base =
    "inline-flex items-center justify-center gap-2 w-full rounded-lg px-5 py-3 text-[14px] font-semibold transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0E1016] disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary:
      "text-white shadow-[0_4px_16px_rgba(99,102,241,0.35)] hover:brightness-110 focus-visible:ring-[#6D5EF6] active:scale-[0.99]",
    outline:
      "bg-[#151A24] text-[#F4F5F8] border border-[#2A3040] hover:bg-[#0E1016] hover:border-[#39415A] focus-visible:ring-[#39415A]",
  };
  const primaryStyle = variant === "primary" ? { backgroundImage: ACCENT_GRADIENT, ...(style || {}) } : style;
  return (
    <button
      disabled={disabled || loading}
      className={`${base} ${variants[variant]} ${className}`}
      style={primaryStyle}
      {...props}
    >
      {loading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        children
      )}
    </button>
  );
}

export function GoogleButton({ onClick, loading, label = "Continue with Google" }) {
  return (
    <Button variant="outline" onClick={onClick} loading={loading} data-testid="google-btn">
      <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.26 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
        <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
      </svg>
      {label}
    </Button>
  );
}

export function TextField({ label, hint, error, id, className = "", ...props }) {
  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-[#C4C9D4]">
          {label}
        </label>
      )}
      <input
        id={id}
        className={`w-full rounded-lg border bg-[#151A24] px-3.5 py-3 text-[15px] text-[#F4F5F8] placeholder-[#6E7686] outline-none transition-colors focus:border-[#6D5EF6] focus:ring-1 focus:ring-[#6D5EF6] ${
          error ? "border-red-400" : "border-[#2A3040]"
        }`}
        {...props}
      />
      {error ? (
        <p className="mt-1.5 text-[12px] text-red-500">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-[12px] text-[#6E7686]">{hint}</p>
      ) : null}
    </div>
  );
}

export function Divider({ label = "OR" }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="h-px flex-1 bg-[#242A38]" />
      <span className="text-[11px] font-medium uppercase tracking-widest text-[#6E7686]">{label}</span>
      <span className="h-px flex-1 bg-[#242A38]" />
    </div>
  );
}

export function FullScreenLoader({ label }) {
  return (
    <div className="min-h-screen grid place-items-center bg-[#151A24]">
      <div className="flex flex-col items-center gap-4">
        <span
          className="h-7 w-7 animate-spin rounded-full border-2 border-[#6D5EF6] border-t-transparent"
        />
        {label && <p className="text-[13px] text-[#98A0B0]">{label}…</p>}
      </div>
    </div>
  );
}
